import React, { useState, useEffect } from "react";
import { 
  DataTable, Column, Dialog, Button, Dropdown, Calendar, InputTextarea,
  ProgressSpinner, Card, Badge, Toolbar, TabView, TabPanel
} from "primereact";
import { toast } from "react-toastify";
import { 
  getLeavesForAdmin, getLeavesForUser, createLeave, updateLeave, deleteLeave, 
  getParentChildren, updateLeaveStatus
} from "../../api/leaveApi";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "../../styles/LeaveList.scss";
import { useAuth } from "../../common/contexts/AuthContext";

const LeaveList = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [children, setChildren] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [activeTab, setActiveTab] = useState("staff");
  const [formData, setFormData] = useState({
    id: null, 
    leaveType: '', 
    fromDate: null, 
    toDate: null, 
    reason: '', 
    studentId: null
  });
  
  const { user } = useAuth();
  const currentUser = user;
  
  const leaveTypes = [
    { label: "Sick Leave", value: "sick" }, 
    { label: "Casual Leave", value: "casual" },
    { label: "Medical Leave", value: "medical" }, 
    { label: "Emergency Leave", value: "emergency" },
    { label: "Maternity Leave", value: "maternity" }, 
    { label: "Paternity Leave", value: "paternity" },
    { label: "Study Leave", value: "study" }, 
    { label: "Unpaid Leave", value: "unpaid" },
    { label: "Other", value: "other" }
  ];

  const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Cancelled', value: 'cancelled' }
  ];

  useEffect(() => {
    loadData();
  }, [currentUser, activeTab]);

  const loadData = async () => {
    console.log("loadData called with user:", currentUser);
    try {
      setLoading(true);
      if (currentUser?.role === "Parent") {
        await loadChildren();
      }
      console.log("Loading leaves for user:", currentUser);
      await loadLeaves();
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadLeaves = async () => {
    try {
      let response;
      const userRole = currentUser?.role?.name || currentUser?.role;
      
      console.log("Current user role:", userRole);
      console.log("Current user:", currentUser);
      console.log("type of ",typeof(userRole));
      // console.log("userRole===Student",userRole==="Student");
      console.log("userRole===Sudent",userRole==="Student");
      
      switch(userRole) {
        case "Admin":
          response = await getLeavesForAdmin();
          break;
        case "SuperAdmin":
          response = await getLeavesForAdmin();
          break;
        case "Teacher":
          // Teacher sees own leaves and Student leaves
          response = await getLeavesForUser(currentUser.id, 'Teacher');
          break;
        case "Parent":
          // Parent sees children's leaves
          if (children && children.length > 0) {
            const leavesForChildren = [];
            // Fetch leaves for each child
            for (const child of children) {
              try {
                const childLeaves = await getLeavesForUser(child.id, 'Student');
                if (childLeaves && Array.isArray(childLeaves)) {
                  leavesForChildren.push(...childLeaves);
                }
              } catch (err) {
                console.error(`Error loading leaves for child ${child.id}:`, err);
              }
            }
            response = leavesForChildren;
          } else {
            response = [];
          }
          break;
        case "Student":
          // Student sees own leaves
          console.log("Fetching leaves for Student with ID:", currentUser.id);
          response = await getLeavesForUser(currentUser.id, 'Student');
          break;
        default:
          response = [];
      }

      console.log("Loaded leaves:", response);
      setLeaves(response || []);
    } catch (err) {
      console.error("Error loading leaves:", err);
      toast.error("Failed to load leaves");
      setLeaves([]);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await getParentChildren(currentUser.id);
      console.log("Loaded children:", response);
      setChildren(response || []);
    } catch (err) {
      console.error("Error loading children:", err);
      toast.error("Failed to load children");
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setSelectedLeave(null);
    setFormData({ 
      id: null, 
      leaveType: '', 
      fromDate: null, 
      toDate: null, 
      reason: '', 
      studentId: null 
    });
    setIsModalVisible(true);
  };

  const handleEdit = (row) => {
    setIsEditing(true);
    setSelectedLeave(row);
    setFormData({
      id: row.id,
      leaveType: row.leaveType,
      fromDate: row.fromDate ? new Date(row.fromDate) : null,
      toDate: row.toDate ? new Date(row.toDate) : null,
      reason: row.reason || '',
      studentId: row.studentId || (row.Student ? row.Student.id : null)
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this leave request?")) return;
    try {
      setLoading(true);
      await deleteLeave(id);
      toast.success("Leave deleted successfully");
      // Update local state
      setLeaves(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete leave");
    } finally { 
      setLoading(false); 
    }
  };

  const handleSubmit = async () => {
    const errors = [];
    if (!formData.leaveType) errors.push("Leave type is required");
    if (!formData.fromDate) errors.push("Start date is required");
    if (!formData.toDate) errors.push("End date is required");
    if (!formData.reason.trim()) errors.push("Reason is required");
    if (currentUser?.role === "Parent" && !formData.studentId) errors.push("Please select a Student");
    
    if (errors.length > 0) { 
      toast.error(errors.join(", ")); 
      return; 
    }
    
    if (new Date(formData.fromDate) > new Date(formData.toDate)) { 
      toast.error("Start date must be before end date"); 
      return; 
    }

    try {
      setLoading(true);
      const payload = {
        leaveType: formData.leaveType,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        reason: formData.reason,
        status: isEditing ? selectedLeave.status : 'pending'
      };
      
      // Add user-specific data based on role
      if (currentUser?.role === "Parent" && formData.studentId) {
        payload.studentId = formData.studentId;
        payload.userId = currentUser.id; // Parent is applying
      } else if (currentUser?.role === "Student") {
        payload.userId = currentUser.id;
      } else if (currentUser?.role === "Teacher") {
        payload.userId = currentUser.id; // Teacher's own leave
      } else if (currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin") {
        // Admin can create leave for any user
        payload.userId = currentUser.id;
      }

      if (isEditing && formData.id) {
        const updatedLeave = await updateLeave(formData.id, payload);
        toast.success("Leave updated successfully!");
        
        // Update in local state
        setLeaves(prev => prev.map(l => 
          l.id === formData.id ? { ...l, ...updatedLeave } : l
        ));
      } else {
        const newLeave = await createLeave(payload);
        toast.success("Leave application submitted!");
        
        // Add to local state
        setLeaves(prev => [...prev, newLeave]);
      }
      
      setIsModalVisible(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Failed to submit leave");
    } finally { 
      setLoading(false); 
    }
  };

  const handleStatusChange = async (leaveId, newStatus) => {
    console.log(`Changing status for leave ${leaveId} to ${newStatus}`);
    
    try {
      setLoading(true);
      const updatedLeave = await updateLeaveStatus(leaveId, { status: newStatus });
      
      toast.success("Status updated!");
      
      // Update local state with the returned data
      setLeaves(prev => prev.map(leave => 
        leave.id === leaveId ? { ...leave, ...updatedLeave } : leave
      ));
    } catch (err) {
      console.error("Error updating status:", err);
      if (err.response?.status === 404) {
        toast.error("Leave not found. Please refresh the page.");
      } else {
        toast.error(err.response?.data?.message || "Failed to update status");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const map = { 
      pending: 'warning', 
      approved: 'success', 
      rejected: 'danger', 
      cancelled: 'secondary' 
    };
    return <Badge value={status?.toUpperCase()} severity={map[status?.toLowerCase()] || 'info'} />;
  };

  const getLeaveTypeBadge = (type) => {
    const colors = { 
      sick: 'info', 
      casual: 'success', 
      medical: 'help', 
      emergency: 'danger', 
      maternity: 'warning', 
      paternity: 'warning', 
      study: 'info', 
      unpaid: 'secondary', 
      other: 'contrast' 
    };
    return <Badge value={type} severity={colors[type] || 'info'} />;
  };

  const getUserName = (row) => {
    // Check different possible user object structures
    if (row.user?.name) return row.user.name;
    if (row.Student?.full_name) return row.Student.full_name; // Changed from name to full_name
    if (row.Teacher?.name) return row.Teacher.name;
    if (row.createdBy?.name) return row.createdBy.name;
    return row.userId || "Unknown";
  };

  // Get user role for display - FIXED VERSION
  const getUserRole = (row) => {
    // Agar Student hai to "Student" dikhao
    if (row.studentId || row.Student) {
      return "Student";
    }
    
    // Agar Teacher object hai to "Teacher" dikhao
    if (row.Teacher) {
      return "Teacher";
    }
    
    // Agar user object hai aur usme role hai
    if (row.user) {
      // Agar role object hai (role: {name: "Admin"})
      if (row.user.role && typeof row.user.role === 'object') {
        return row.user.role.name || "Staff";
      }
      // Agar role string hai (role: "Admin")
      else if (row.user.role && typeof row.user.role === 'string') {
        return row.user.role;
      }
    }
    
    // Agar createdBy object hai
    if (row.createdBy) {
      if (row.createdBy.role && typeof row.createdBy.role === 'object') {
        return row.createdBy.role.name || "Staff";
      }
      else if (row.createdBy.role && typeof row.createdBy.role === 'string') {
        return row.createdBy.role;
      }
    }
    
    // Default case - API se jo role aaye woh display karo
    return row.role || "Staff";
  };

  // Filter leaves based on user role and active tab
  const filteredLeaves = leaves.filter(leave => {
    const userRole = currentUser?.role?.name || currentUser?.role;
    console.log("user role",userRole,"activeTab",activeTab,"leave",leave);
    
    
    if (userRole === "Admin" || userRole === "SuperAdmin") {
      if (activeTab === "staff") {
        return !leave.studentId && !leave.Student;
      } else if (activeTab === "Student") {
        return leave.studentId || leave.Student;
      }
      return true;
    }
    
    if (userRole === "Teacher") {
      // Teacher sees their own leaves and Student leaves
      const isOwnLeave = leave.userId === currentUser.id && !leave.studentId;
      const isStudentLeave = leave.studentId || leave.Student;
      
      if (activeTab === "staff") return isOwnLeave;
      if (activeTab === "Student") return isStudentLeave;
      
      return isOwnLeave || isStudentLeave;
    }
    
    if (userRole === "Parent") {
      // Parent sees only their children's leaves
      if (!children || children.length === 0) return false;
      const childIds = children.map(child => child.id);
      return childIds.includes(leave.studentId) || 
             (leave.Student && childIds.includes(leave.Student.id));
    }
    
    if (userRole === "Student") {
      return leave.userId === currentUser.id || leave.studentId === currentUser.id;
    }
    
    return false;
  });

  // Calculate counts
  const totalCount = filteredLeaves.length;
  const pendingCount = filteredLeaves.filter(l => l.status === 'pending').length;
  const approvedCount = filteredLeaves.filter(l => l.status === 'approved').length;

  const leftToolbarTemplate = () => (
    <Button 
      label="Apply Leave" 
      icon="pi pi-plus" 
      onClick={handleCreate} 
      className="p-button-primary" 
      disabled={loading || (currentUser?.role === "Parent" && children.length === 0)} 
    />
  );

  const actionBodyTemplate = (row) => {
    const userRole = currentUser?.role?.name || currentUser?.role;
    const canEdit = row.status === 'pending' || 
                   userRole === 'Admin' || 
                   userRole === 'SuperAdmin';
    
    return (
      <div className="flex gap-1 align-items-center">
        <Button 
          icon="pi pi-pencil" 
          className="p-button-rounded p-button-text p-button-primary" 
          onClick={() => handleEdit(row)} 
          disabled={!canEdit || loading}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
        />
        <Button 
          icon="pi pi-trash" 
          className="p-button-rounded p-button-text p-button-danger" 
          onClick={() => handleDelete(row.id)} 
          disabled={row.status !== 'pending' || loading}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  // Status column template with dropdown for admins
  const statusBodyTemplate = (row) => {
    const userRole = currentUser?.role?.name || currentUser?.role;
    
    if (userRole === 'Admin' || userRole === 'SuperAdmin') {
      return (
        <Dropdown 
          value={row.status} 
          options={statusOptions} 
          onChange={(e) => handleStatusChange(row.id, e.value)}
          className="status-dropdown"
          disabled={loading}
          style={{ 
            width: '100%', 
            minWidth: '120px',
            border: '1px solid #e5e7eb',
            borderRadius: '6px'
          }}
        />
      );
    } else {
      return getStatusBadge(row.status);
    }
  };

  return (
    <div className="leave-management">
      <Card className="shadow-2">
        <div className="flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold">
              <i className="pi pi-calendar mr-2"></i>
              Leave Management
            </h2>
            <p className="text-color-secondary">
              {currentUser?.role === 'Admin' || currentUser?.role === 'SuperAdmin' 
                ? 'Manage all leave applications' 
                : 'View and manage your leave applications'}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="stat-card">
              <span className="stat-value">{totalCount}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-card pending">
              <span className="stat-value">{pendingCount}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-card approved">
              <span className="stat-value">{approvedCount}</span>
              <span className="stat-label">Approved</span>
            </div>
          </div>
        </div>

        <Toolbar left={leftToolbarTemplate} className="mb-4" />

        {(currentUser?.role === "Admin" || 
          currentUser?.role === "SuperAdmin" || 
          currentUser?.role === "Teacher") && (
          <TabView 
            activeIndex={activeTab === "staff" ? 0 : 1} 
            onTabChange={(e) => setActiveTab(e.index === 0 ? "staff" : "Student")}
            className="mb-4"
          >
            <TabPanel header={currentUser?.role === "Teacher" ? "Your Leaves" : "Staff"} />
            <TabPanel header="Students" />
          </TabView>
        )}

        {loading ? (
          <div className="flex justify-content-center align-items-center py-5">
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable 
            value={filteredLeaves} 
            paginator 
            rows={10} 
            responsiveLayout="scroll"
            className="p-datatable-striped"
            emptyMessage="No leaves found."
          >
            <Column field="id" header="ID" sortable style={{ width: '80px' }} />
            <Column 
              header="Name" 
              body={(row) => getUserName(row)}
              sortable 
              style={{ width: '150px' }} 
            />
            <Column 
              header="Role" 
              body={(row) => getUserRole(row)}
              sortable 
              style={{ width: '120px' }} 
            />
            <Column 
              header="Leave Type" 
              body={(row) => getLeaveTypeBadge(row.leaveType)} 
              sortable 
              style={{ width: '150px' }} 
            />
            <Column 
              header="From Date" 
              body={(row) => formatDate(row.fromDate)} 
              sortable 
              style={{ width: '120px' }} 
            />
            <Column 
              header="To Date" 
              body={(row) => formatDate(row.toDate)} 
              sortable 
              style={{ width: '120px' }} 
            />
            <Column 
              header="Status" 
              body={statusBodyTemplate} 
              sortable 
              style={{ width: '150px' }} 
            />
            <Column 
              header="Reason" 
              body={(row) => row.reason?.substring(0, 50) + (row.reason?.length > 50 ? '...' : '')}
              style={{ width: '200px' }} 
            />
            <Column 
              header="Action" 
              body={actionBodyTemplate} 
              style={{ width: '120px', textAlign: 'center' }} 
            />
          </DataTable>
        )}
      </Card>

      {/* Leave Form Modal */}
      <Dialog 
        header={isEditing ? "Edit Leave" : "Apply Leave"} 
        visible={isModalVisible} 
        style={{ width: '500px' }} 
        onHide={() => !loading && setIsModalVisible(false)} 
        modal 
        className="p-fluid"
      >
        <div className="formgrid grid">
          {currentUser?.role === "Parent" && children.length > 0 && (
            <div className="field col-12">
              <label>Student *</label>
              <Dropdown 
                value={formData.studentId} 
                options={children.map(c => ({ label: c.full_name || c.name, value: c.id }))} 
                onChange={(e) => setFormData({ ...formData, studentId: e.value })} 
                className="w-full" 
                disabled={loading} 
                placeholder="Select Student"
              />
            </div>
          )}
          
          <div className="field col-12">
            <label>Leave Type *</label>
            <Dropdown 
              value={formData.leaveType} 
              options={leaveTypes} 
              onChange={(e) => setFormData({ ...formData, leaveType: e.value })} 
              placeholder="Select Leave Type" 
              className="w-full" 
              disabled={loading} 
            />
          </div>
          
          <div className="field col-12 md:col-6">
            <label>From Date *</label>
            <Calendar 
              value={formData.fromDate} 
              onChange={(e) => setFormData({ ...formData, fromDate: e.value })} 
              dateFormat="dd/mm/yy" 
              showIcon 
              className="w-full" 
              minDate={new Date()} 
              disabled={loading} 
            />
          </div>
          
          <div className="field col-12 md:col-6">
            <label>To Date *</label>
            <Calendar 
              value={formData.toDate} 
              onChange={(e) => setFormData({ ...formData, toDate: e.value })} 
              dateFormat="dd/mm/yy" 
              showIcon 
              className="w-full" 
              minDate={formData.fromDate || new Date()} 
              disabled={loading} 
            />
          </div>
          
          <div className="field col-12">
            <label>Reason *</label>
            <InputTextarea 
              value={formData.reason} 
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })} 
              rows={4} 
              placeholder="Reason..." 
              className="w-full" 
              disabled={loading}
              maxLength={500}
            />
            <small className="text-color-secondary">
              {formData.reason.length}/500 characters
            </small>
          </div>
          
          <div className="field col-12 flex justify-content-end gap-2 mt-4">
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              onClick={() => setIsModalVisible(false)} 
              disabled={loading} 
              className="p-button-secondary"
            />
            <Button 
              label={isEditing ? "Update" : "Submit"} 
              icon={isEditing ? "pi pi-check" : "pi pi-send"} 
              onClick={handleSubmit} 
              loading={loading} 
              disabled={loading}
              className="p-button-primary"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LeaveList;